import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { DateTime } from 'luxon'
import * as XLSX from 'xlsx'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import {
  Alert,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  AlternateEmail as AlternateEmailIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  EmojiTransportation as TransportationIcon,
  FileDownload as FileDownloadIcon,
  LocationOn as LocationOnIcon,
  PageviewOutlined as PageviewIcon,
  Person as PersonIcon,
  Menu as MenuIcon
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import SwipeableCalendar from '../../common/components/SwipeableCalendar/SwipeableCalendar'
import MessageBox from '../../common/components/MessageBox/MessageBox'
import SwipeableTimePicker from '../../common/components/SwipeableTimePicker/SwipeableTimePicker'
import LayoutCreate from '../../common/components/LayoutCreate/LayoutCreate'
import DragAndDrop from '../../common/components/DragAndDrop/DragAndDrop'
import CustomizedStepper from '../../common/components/CustomizedStepper/CustomizedStepper'
import PrintVisit from './PrintVisit'
import db from '../../db/db'
import { api } from '../../api/api'
import { defaultCatch, defaultSuccess, setLoading, setSnackbar } from '../../state/actions'
import {
  DEFAULT_AUTOCOMPLETE,
  IMPORT,
  MAX_LICENSE_PLATE_LENGTH,
  MAX_NOTES_LENGTH,
  REGEX_LICENSE_PLATE,
  VISIT,
} from '../../variables'
import { formatDateToISOShort, formatDateToMed, getFormateDate, getTimeDuration, formatDateAndTimeToISO, isTodayFromJS, today, formatDateToTimeISO, getCurrentHour } from '../../common/utilities/FormatDate'
import { attachInvitationQrValidity } from '../../common/utilities/invitationValidations'
import { isRetryableInvitationNetworkError, isSingleInvitationDay } from './invitationSaveGuards'
import { debounce, downloadQRAsImage, getAccessControlQR, getNormalizedEmailList, getWorkplaceDefaultEntryTime, getWorkplaceDefaultExitTime, getWorkplaceReasons, isTodayFromJsDate, isValidEmailList } from '../../common/utilities/utilities'

import './NewVisitor.sass'

const DEFAULT_ENTRY_TIME = '08:00'
const DEFAULT_EXIT_TIME = '09:00'
const stackProps = {
  className: 'visitor-field-row',
}
const destinationMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 380,
    },
  },
}


const eachDayInclusive = (start, end) => {
  const days = []
  let cursor = DateTime.fromJSDate(start).startOf('day')
  const endDay = DateTime.fromJSDate(end).startOf('day')
  while (cursor <= endDay) {
    days.push(cursor.toJSDate())
    cursor = cursor.plus({ days: 1 })
  }
  return days
}

function NewVisitor(props) {
  const {
    ask_visit_comes_from = false,
    ask_visit_identification = false,
    ask_visit_license_plate = false,
    building_id,
    companies = [],
    custom_visit_identification_label = '',
    defaultCatch,
    defaultSuccess,
    isCostaRica = false,
    isOnline = true,
    onCancel,
    onCreated,
    setLoading,
    setSnackbar,
    visitDestinations = [],
    workplaceDefaultEntryTime,
    workplaceDefaultExitTime
  } = props

  const getDefaultEntryTime = useCallback((date) => {
    const isTodayValue = isTodayFromJsDate(date)

    if (workplaceDefaultEntryTime && !isTodayValue) {
      return workplaceDefaultEntryTime
    }

    if (isTodayValue) {
      const now = getCurrentHour(0, 10)
      return workplaceDefaultEntryTime && workplaceDefaultEntryTime >= now
        ? workplaceDefaultEntryTime
        : now
    }

    return workplaceDefaultEntryTime || new Date().setHours(8, 0, 0, 0)
  }, [workplaceDefaultEntryTime])

  const { t } = useTranslation()
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('md'))
  const identificationLabel = custom_visit_identification_label || t('Identification')
  const reasons = getWorkplaceReasons(t)

  const toTimeString = useCallback((value, fallback) => {
    if (!value) return fallback

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`
    }

    if (typeof value === 'string') {
      const isoMatch = value.match(/(\d{2}):(\d{2})/)
      if (isoMatch) {
        return `${isoMatch[1]}:${isoMatch[2]}`
      }

      const parsed = new Date(value)
      if (!Number.isNaN(parsed.getTime())) {
        return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`
      }
    }

    if (typeof value === 'number') {
      const parsed = new Date(value)
      if (!Number.isNaN(parsed.getTime())) {
        return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`
      }
    }

    return fallback
  }, [])

  const formatDisplayTime = useCallback((timeValue) => {
    if (!timeValue || typeof timeValue !== 'string') return ''

    const [hoursString, minutesString] = timeValue.split(':')
    const hours = Number(hoursString)
    const minutes = Number(minutesString)

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeValue

    const period = hours >= 12 ? 'pm' : 'am'
    const displayHours = hours % 12 || 12

    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`
  }, [])

  // Replica el guard de CorporateExperienceWebapp (NewVisitor.js `onContinue`):
  // "Hasta" nunca se deriva de "Desde" por si sola, por lo que si el "Desde"
  // calculado cae por encima del "Hasta" recibido (workplace default o el
  // valor previo del picker), se recorre "Hasta" a "Desde + 1 hora" en vez de
  // dejar el formulario nacer en un estado invalido.
  const buildGuardedEntryExitTimes = useCallback((baseDate, previousExitTimePickerValue) => {
    const rawEntry = getDefaultEntryTime(baseDate)
    const entryDate = rawEntry instanceof Date ? rawEntry : new Date(rawEntry)

    const previousExit = previousExitTimePickerValue instanceof Date && !Number.isNaN(previousExitTimePickerValue.getTime())
      ? previousExitTimePickerValue
      : null

    let exitDate = previousExit
    if (!exitDate || exitDate < entryDate) {
      exitDate = new Date(entryDate)
      exitDate.setHours(exitDate.getHours() + 1)
    }

    return { entryTimePickerValue: entryDate, exitTimePickerValue: exitDate }
  }, [getDefaultEntryTime])

  const [tab, setTab] = useState(VISIT)
  const [date, setDate] = useState(today)
  const [calendarDate, setCalendarDate] = useState(today)
  const [openCalendar, setOpenCalendar] = useState(false)
  const [openTimePicker, setOpenTimePicker] = useState(false)
  const [entryTimePickerValue, setEntryTimePickerValue] = useState(
    () => buildGuardedEntryExitTimes(today, workplaceDefaultExitTime).entryTimePickerValue
  )
  const [exitTimePickerValue, setExitTimePickerValue] = useState(
    () => buildGuardedEntryExitTimes(today, workplaceDefaultExitTime).exitTimePickerValue
  )
  const [entryTime, setEntryTime] = useState(() => toTimeString(entryTimePickerValue, DEFAULT_ENTRY_TIME))
  const [exitTime, setExitTime] = useState(() => toTimeString(exitTimePickerValue, DEFAULT_EXIT_TIME))
  const [identification, setIdentification] = useState('')
  const [name, setName] = useState('')
  const [lastNameOne, setLastNameOne] = useState('')
  const [lastNameTwo, setLastNameTwo] = useState('')
  const [email, setEmail] = useState('')
  const [comesFrom, setComesFrom] = useState('')
  const [licensePlate, setLicensePlate] = useState('')
  const [notes, setNotes] = useState('')
  const [hostNotes, setHostNotes] = useState('')
  const [enabledInvitationNotes, setEnabledInvitationNotes] = useState(false)
  const [enabledInvitationHostNotes, setEnabledInvitationHostNotes] = useState(false)
  const [visitReason, setVisitReason] = useState(reasons[0])
  const [destinationId, setDestinationId] = useState('')
  const [emailError, setEmailError] = useState('')
  const [licensePlateError, setLicensePlateError] = useState('')
  const [file, setFile] = useState(null)
  const [invitations, setInvitations] = useState([])
  const [activeStep, setActiveStep] = useState(0)
  const [successItem, setSuccessItem] = useState(null)
  const [accessControlQR, setAccessControlQR] = useState(null)
  const [successSyncStatus, setSuccessSyncStatus] = useState('synced')
  const [multiDay, setMultiDay] = useState(false)

  const isImportFlow = tab === IMPORT
  const isImportSuccessStep = isImportFlow && activeStep === 1
  const isVisitorSuccessStep = !isImportFlow && activeStep === 2

  const stepperTotalSteps = isImportFlow ? 2 : 3
  const stepperActiveStep = isImportFlow ? Math.min(activeStep, 1) : activeStep
  const destination = useMemo(
    () => visitDestinations.find(item => String(item.id) === String(destinationId)) || DEFAULT_AUTOCOMPLETE,
    [destinationId, visitDestinations]
  )

  useEffect(() => {
    const nextEnabledInvitationNotes = !!destination?.enabled_invitation_notes
    const nextEnabledInvitationHostNotes = !!destination?.enabled_invitation_host_notes

    setEnabledInvitationNotes(nextEnabledInvitationNotes)
    setEnabledInvitationHostNotes(nextEnabledInvitationHostNotes)

    if (!nextEnabledInvitationNotes) setNotes('')
    if (!nextEnabledInvitationHostNotes) setHostNotes('')
  }, [destination])

  function buildInitialTime(timeValue) {
    const [hours, minutes] = timeValue.split(':').map(Number)
    return DateTime.fromJSDate(today)
      .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
      .toJSDate()
  }

  const buildDateTime = useCallback((baseDate, timeValue) => {
    const [hours, minutes] = timeValue.split(':').map(Number)
    return DateTime.fromJSDate(baseDate)
      .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 })
      .toJSDate()
  }, [])

  const buildInvitationTime = useCallback((baseDate, timeValue) => {
    return formatDateAndTimeToISO(baseDate, buildDateTime(baseDate, timeValue))
  }, [buildDateTime])

  const getDuration = useCallback((startTime, endTime) => {
    const start = buildDateTime(today, startTime)
    const end = buildDateTime(today, endTime)
    return getTimeDuration(start, end)
  }, [buildDateTime])

  const resetMessages = useCallback(() => {
    setEmailError('')
    setLicensePlateError('')
  }, [])

  const evalSetHostNotes = useCallback(value => {
    if (value.length > MAX_NOTES_LENGTH) return
    setHostNotes(value)
  }, [])

  const showErrorMessage = useCallback(message => {
    setSnackbar({
      open: true,
      message,
      severity: 'error',
      autoHideDuration: 5000,
    })
  }, [setSnackbar])

  const getDateLabel = useCallback(() => {
    if (Array.isArray(date)) {
      const [start, end] = date
      if (!start || !end) return ''
      const startLabel = isTodayFromJS(start) ? t('today') : formatDateToMed(start)
      const endLabel = isTodayFromJS(end) ? t('today') : formatDateToMed(end)
      return `${startLabel} - ${endLabel}`
    }
    return isTodayFromJS(date) ? t('today') : formatDateToMed(date)
  }, [date, t])

  // Alineado con CorporateExperienceWebapp: acepta listas de correos
  // separadas por coma (`isValidEmailList`) en vez del regex mas estricto
  // de un solo correo que se usaba antes.
  const validateEmail = useCallback(value => isValidEmailList(value), [])

  const validateLicensePlate = useCallback(value => !value || REGEX_LICENSE_PLATE.test(value), [])

  const validateVisitForm = useCallback(() => {
    resetMessages()

    if (ask_visit_identification && !identification.trim()) {
      showErrorMessage(t('Complete required fields'))
      return false
    }

    if (!name.trim() || !lastNameOne.trim() || !email.trim() || destination.id === -1) {
      showErrorMessage(t('Complete required fields'))
      return false
    }

    if (ask_visit_comes_from && !comesFrom.trim()) {
      showErrorMessage(t('Complete required fields'))
      return false
    }

    if (!validateEmail(email)) {
      setEmailError(t('invalid email'))
      return false
    }

    if (!validateLicensePlate(licensePlate)) {
      setLicensePlateError(t('invalid license plate'))
      return false
    }

    if (exitTime <= entryTime) {
      showErrorMessage(t('Check out time must be greater than check in time'))
      return false
    }

    return true
  }, [
    ask_visit_comes_from,
    ask_visit_identification,
    comesFrom,
    destination.id,
    email,
    entryTime,
    exitTime,
    identification,
    lastNameOne,
    licensePlate,
    name,
    resetMessages,
    showErrorMessage,
    t,
    validateEmail,
    validateLicensePlate,
  ])

  const storeInvitationsLocally = useCallback(async items => {
    if (!items.length) return
    const normalized = items.map(item => attachInvitationQrValidity({
      ...item,
      id: item.id ?? `local:${crypto.randomUUID()}`,
      date: item.date || item.invitation_date || formatDateToISOShort(Array.isArray(date) ? date[0] : date),
      syncStatus: item.syncStatus || 'synced',
    }, companies))
    await db.invitations.bulkPut(normalized)
  }, [companies, date])

  const buildLocalImportedInvitations = useCallback(() => {
    return invitations.map(invitation => {
      const uuid = invitation.uuid ?? crypto.randomUUID()
      return attachInvitationQrValidity({
        ...invitation,
        id: invitation.id ?? `local:${uuid}`,
        uuid,
        date: invitation.date || invitation.invitation_date,
        company_id: invitation.company_id || destination.company_id,
        syncStatus: 'pending',
        syncMode: 'batch',
        destination,
      }, companies)
    })
  }, [companies, destination, invitations])

  const buildLocalInvitationRecord = useCallback(() => {
    const targetDate = Array.isArray(date) ? date[0] : date
    const uuid = crypto.randomUUID()

    return attachInvitationQrValidity({
      id: `local:${uuid}`,
      uuid,
      syncStatus: 'pending',
      syncMode: 'range',
      date: formatDateToISOShort(targetDate),
      invitation_date: formatDateToISOShort(targetDate),
      first_name: name.trim(),
      last_name_one: lastNameOne.trim(),
      last_name_two: lastNameTwo.trim(),
      email: getNormalizedEmailList(email),
      identification: identification.trim(),
      invitation_time: buildInvitationTime(targetDate, entryTime),
      comes_from: ask_visit_comes_from ? comesFrom.trim() : 'PARSO',
      duration: getDuration(entryTime, exitTime),
      destination_id: destination.id,
      building_id: destination.building_id || building_id,
      company_id: destination.company_id,
      destination,
      ...(licensePlate ? { license_plate: licensePlate.trim().toUpperCase() } : {}),
      ...(visitReason ? { reason: visitReason } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(hostNotes.trim() ? { host_notes: hostNotes.trim() } : {}),
    }, companies)
  }, [
    ask_visit_comes_from,
    buildInvitationTime,
    building_id,
    companies,
    comesFrom,
    date,
    destination,
    email,
    entryTime,
    exitTime,
    getDuration,
    hostNotes,
    identification,
    lastNameOne,
    lastNameTwo,
    licensePlate,
    name,
    notes,
    visitReason,
  ])

  const ensureInvitationRequestSucceeded = useCallback(response => {
    if (response?.data?.success === true) return true

    const errorMessage = response?.data?.message || t('something went wrong')
    showErrorMessage(errorMessage)
    return false
  }, [showErrorMessage, t])

  const saveSingleInvitation = useCallback(async () => {
    if (!validateVisitForm()) return

    // Alineado con Webapp: si `multiDay` esta activo, `date` es un rango real
    // ([start, end]) y ambos extremos deben viajar al backend — CorporateService
    // (`create_range`) ya itera cada dia del rango y crea una invitacion por
    // dia. Antes esto siempre colapsaba a un solo dia (start = end = date[0]).
    const targetDate = Array.isArray(date) ? date[0] : date
    const endDate = Array.isArray(date) ? (date[1] || date[0]) : date
    const localInvitation = buildLocalInvitationRecord()
    const payload = {
      start_date: formatDateToISOShort(targetDate),
      end_date: formatDateToISOShort(endDate),
      invitation: {
        first_name: localInvitation.first_name,
        last_name_one: localInvitation.last_name_one,
        last_name_two: localInvitation.last_name_two,
        email: localInvitation.email,
        identification: localInvitation.identification,
        invitation_time: localInvitation.invitation_time,
        comes_from: localInvitation.comes_from,
        duration: localInvitation.duration,
        destination_id: localInvitation.destination_id,
        building_id: localInvitation.building_id,
        uuid: localInvitation.uuid,
        ...(localInvitation.license_plate ? { license_plate: localInvitation.license_plate } : {}),
        ...(localInvitation.reason ? { reason: localInvitation.reason } : {}),
        ...(localInvitation.notes ? { notes: localInvitation.notes } : {}),
        ...(localInvitation.host_notes ? { host_notes: localInvitation.host_notes } : {}),
      },
    }

    if (!isOnline) {
      await db.invitations.put(localInvitation)
      setSuccessItem(localInvitation)
      setSuccessSyncStatus('pending')
      setAccessControlQR(null)
      setActiveStep(2)
      setSnackbar({
        open: true,
        message: t('Pre-register saved offline. It will sync when connection is restored.'),
        severity: 'success',
        autoHideDuration: 5000,
      })
      return
    }

    try {
      setLoading(true)
      const response = await api.post('/invitations/range', payload)
      if (!ensureInvitationRequestSucceeded(response)) return
      defaultSuccess(response)
      const created = response?.data?.invitations || response?.data?.invitation || []
      await storeInvitationsLocally(created)
      if (created[0]?.id) {
        await getAccessControlQR(created[0].id, api, {
          defaultSuccess,
          defaultCatch,
          setAccessControlQR,
        })
      }
      setSuccessItem(created[0] || payload.invitation)
      setSuccessSyncStatus('synced')
      setActiveStep(2)
      setSnackbar({
        open: true,
        message: t('Pre-register created successfully'),
        severity: 'success',
        autoHideDuration: 4000,
      })
    } catch (error) {
      if (isSingleInvitationDay(date) && isRetryableInvitationNetworkError(error)) {
        await db.invitations.put(localInvitation)
        defaultCatch(error)
        setSuccessItem(localInvitation)
        setSuccessSyncStatus('pending')
        setAccessControlQR(null)
        setActiveStep(2)
        setSnackbar({
          open: true,
          message: t('Pre-register saved offline. It will sync when connection is restored.'),
          severity: 'success',
          autoHideDuration: 5000,
        })
        return
      }

      defaultCatch(error)
      showErrorMessage(error?.response?.data?.message || t('something went wrong'))
    } finally {
      setLoading(false)
    }
  }, [
    isOnline,
    validateVisitForm,
    date,
    setLoading,
    defaultSuccess,
    storeInvitationsLocally,
    buildLocalInvitationRecord,
    ensureInvitationRequestSucceeded,
    showErrorMessage,
    setSnackbar,
    t,
    defaultCatch,
    setSuccessItem,
    setSuccessSyncStatus,
    setAccessControlQR,
    setActiveStep,
  ])

  const showFileError = useCallback((message = t('The file format is incorrect. Please download the template and place the information based on the template')) => {
    setSnackbar({
      open: true,
      message,
      severity: 'error',
      autoHideDuration: 5000,
    })
  }, [setSnackbar, t])

  const parseImportedInvitations = useCallback((rows = []) => {
    if (!rows.length) {
      showFileError(t('Empty file'))
      return []
    }

    const selectedDates = Array.isArray(date) ? eachDayInclusive(date[0], date[1]) : [date]
    if (!selectedDates.length || !selectedDates[0]) {
      showFileError(t('Select the date.'))
      return []
    }

    const parsed = []

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index]
      const identificationValue = row.identification || ''
      const firstName = row.first_name || ''
      const lastNameOneValue = row.last_name_one || ''
      const lastNameTwoValue = row.last_name_two || ''
      const emailValue = getNormalizedEmailList(row.email || '')
      const comesFromValue = row.comes_from || ''
      const licensePlateValue = (row.license_plate || '').toUpperCase()

      const invalidRequired =
        (ask_visit_identification && !identificationValue) ||
        !firstName ||
        !lastNameOneValue ||
        !emailValue ||
        (ask_visit_comes_from && !comesFromValue)

      if (invalidRequired || !validateEmail(emailValue) || !validateLicensePlate(licensePlateValue)) {
        showFileError(t('The file format is incorrect, The x property in the y row is invalid.', { index: index + 2 }))
        return []
      }

      selectedDates.forEach(selectedDate => {
        parsed.push({
          identification: identificationValue,
          first_name: firstName,
          last_name_one: lastNameOneValue,
          last_name_two: lastNameTwoValue,
          email: emailValue,
          invitation_date: formatDateToISOShort(selectedDate),
          invitation_time: buildInvitationTime(selectedDate, entryTime),
          duration: getDuration(entryTime, exitTime),
          comes_from: ask_visit_comes_from ? comesFromValue : 'PARSO',
          destination_id: destination.id,
          building_id: destination.building_id || building_id,
          ...(licensePlateValue ? { license_plate: licensePlateValue } : {}),
          ...(visitReason ? { reason: visitReason } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          ...(hostNotes.trim() ? { host_notes: hostNotes.trim() } : {}),
        })
      })
    }

    return parsed
  }, [
    ask_visit_comes_from,
    ask_visit_identification,
    building_id,
    buildInvitationTime,
    date,
    destination.building_id,
    destination.id,
    entryTime,
    exitTime,
    getDuration,
    hostNotes,
    notes,
    visitReason,
    showFileError,
    t,
    validateEmail,
    validateLicensePlate,
  ])

  useEffect(() => {
    if (!file) return undefined

    let cancelled = false
    const reader = new FileReader()
    setLoading(true)

    reader.onload = event => {
      if (cancelled) return
      try {
        const workbook = XLSX.read(event.target.result, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const firstSheet = workbook.Sheets[firstSheetName]
        const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '', raw: false })
        const parsedRows = parseImportedInvitations(rows)
        setInvitations(parsedRows)
        setLoading(false)
      } catch (error) {
        if (cancelled) return
        setInvitations([])
        setLoading(false)
        showFileError()
      }
    }

    reader.onerror = () => {
      if (cancelled) return
      setInvitations([])
      setLoading(false)
      showFileError()
    }

    reader.readAsArrayBuffer(file)

    return () => {
      cancelled = true
      if (reader.readyState === FileReader.LOADING) reader.abort()
    }
  }, [file, parseImportedInvitations, setLoading, showFileError])

  const saveImportedInvitations = useCallback(async () => {
    if (!invitations.length || destination.id === -1) {
      showErrorMessage(t('Complete required fields'))
      return
    }

    if (!isOnline) {
      const localInvitations = buildLocalImportedInvitations()
      await storeInvitationsLocally(localInvitations)
      setSuccessItem(localInvitations[0] || invitations[0])
      setSuccessSyncStatus('pending')
      setAccessControlQR(null)
      setActiveStep(1)
      setSnackbar({
        open: true,
        message: t('Pre-register saved offline. It will sync when connection is restored.'),
        severity: 'success',
        autoHideDuration: 5000,
      })
      return
    }

    try {
      setLoading(true)
      const response = await api.post('/invitations/batch', { invitations })
      if (!ensureInvitationRequestSucceeded(response)) return
      defaultSuccess(response)
      const created = response?.data?.invitations || response?.data?.invitation || []
      await storeInvitationsLocally(created)
      if (created[0]?.id) {
        await getAccessControlQR(created[0].id, api, {
          defaultSuccess,
          defaultCatch,
          setAccessControlQR,
        })
      }
      setSuccessItem(created[0] || invitations[0])
      setSuccessSyncStatus('synced')
      setActiveStep(1)
      setSnackbar({
        open: true,
        message: t('Pre-register created successfully'),
        severity: 'success',
        autoHideDuration: 4000,
      })
    } catch (error) {
      defaultCatch(error)
      showErrorMessage(error?.response?.data?.message || t('something went wrong'))
    } finally {
      setLoading(false)
    }
  }, [
    isOnline,
    setSnackbar,
    t,
    invitations,
    destination.id,
    setLoading,
    defaultSuccess,
    storeInvitationsLocally,
    buildLocalImportedInvitations,
    ensureInvitationRequestSucceeded,
    defaultCatch,
    setAccessControlQR,
    setActiveStep,
    setSuccessItem,
    setSuccessSyncStatus,
    showErrorMessage
  ])

  const handleFileSelection = useCallback(selectedFile => {
    setFile(selectedFile)
    setInvitations([])
  }, [])

  const onContinueCalendar = useCallback(() => {
    const nextDate = calendarDate || today
    setDate(nextDate)
    setOpenCalendar(false)

    const rangeAnchor = Array.isArray(nextDate) ? nextDate[0] : nextDate
    const { entryTimePickerValue: newEntry, exitTimePickerValue: newExit } =
      buildGuardedEntryExitTimes(rangeAnchor, exitTimePickerValue)
    setEntryTimePickerValue(newEntry)
    setExitTimePickerValue(newExit)
    setEntryTime(toTimeString(newEntry, DEFAULT_ENTRY_TIME))
    setExitTime(toTimeString(newExit, DEFAULT_EXIT_TIME))
  }, [calendarDate, exitTimePickerValue, buildGuardedEntryExitTimes, toTimeString])

  const onCloseCalendar = useCallback(() => {
    setCalendarDate(date)
    setOpenCalendar(false)
  }, [date])

  const onChangeMultiDay = useCallback(event => {
    const checked = event.target.checked
    const nextDate = checked ? [today, today] : today

    // Alineado con Webapp (`onChangeMultiDay`): usa los defaults reales del
    // workplace en vez de 08:00/09:00 fijos, con el mismo fallback hardcodeado
    // solo por si el dato del workplace aun no llego del backend.
    const nextEntryPickerValue = workplaceDefaultEntryTime || buildInitialTime(DEFAULT_ENTRY_TIME)
    const nextExitPickerValue = workplaceDefaultExitTime || buildInitialTime(DEFAULT_EXIT_TIME)

    setCalendarDate(nextDate)
    setDate(nextDate)
    setEntryTime(toTimeString(nextEntryPickerValue, DEFAULT_ENTRY_TIME))
    setEntryTimePickerValue(nextEntryPickerValue)
    setExitTime(toTimeString(nextExitPickerValue, DEFAULT_EXIT_TIME))
    setMultiDay(checked)
    setExitTimePickerValue(nextExitPickerValue)
  }, [workplaceDefaultEntryTime, workplaceDefaultExitTime, toTimeString])

  const defaultMenuItem = useCallback((label = t('select')) => {
    return (
      <MenuItem value={"-1"} disabled>
        <em>{label}</em>
      </MenuItem>
    )
  }, [t])

  const onContinueTimePicker = useCallback(() => {
    const nextEntry = `${String(entryTimePickerValue.getHours()).padStart(2, '0')}:${String(entryTimePickerValue.getMinutes()).padStart(2, '0')}`
    const nextExit = `${String(exitTimePickerValue.getHours()).padStart(2, '0')}:${String(exitTimePickerValue.getMinutes()).padStart(2, '0')}`
    setEntryTime(nextEntry)
    setExitTime(nextExit)
    setOpenTimePicker(false)
  }, [entryTimePickerValue, exitTimePickerValue])

  const onCloseTimePicker = useCallback(() => {
    setEntryTimePickerValue(buildDateTime(today, entryTime))
    setExitTimePickerValue(buildDateTime(today, exitTime))
    setOpenTimePicker(false)
  }, [buildDateTime, entryTime, exitTime])

  const handleOpenDatePicker = useCallback(() => {
    setOpenCalendar(true)
  }, [])

  const handleOpenTimePicker = useCallback(() => {
    setEntryTimePickerValue(buildDateTime(today, entryTime))
    setExitTimePickerValue(buildDateTime(today, exitTime))
    setOpenTimePicker(true)
  }, [buildDateTime, entryTime, exitTime])

  const handleTabChange = useCallback((_, nextTab) => {
    setMultiDay(false)
    setCalendarDate(today)
    setDate(today)
    setTab(nextTab)
  }, [])

  const handleVisitReasonChange = useCallback(event => {
    setVisitReason(event.target.value)
  }, [])

  const continueVisitorStep = useCallback(() => {
    resetMessages()

    if (ask_visit_identification && !identification.trim()) {
      showErrorMessage(t('Complete required fields'))
      return
    }

    if (!name.trim() || !lastNameOne.trim() || !email.trim() || destination.id === -1) {
      showErrorMessage(t('Complete required fields'))
      return
    }

    if (ask_visit_comes_from && !comesFrom.trim()) {
      showErrorMessage(t('Complete required fields'))
      return
    }

    if (!validateEmail(email)) {
      setEmailError(t('invalid email'))
      return
    }

    if (!validateLicensePlate(licensePlate)) {
      setLicensePlateError(t('invalid license plate'))
      return
    }

    setActiveStep(1)
  }, [
    ask_visit_comes_from,
    ask_visit_identification,
    comesFrom,
    destination.id,
    email,
    identification,
    lastNameOne,
    licensePlate,
    name,
    resetMessages,
    showErrorMessage,
    t,
    validateEmail,
    validateLicensePlate,
  ])

  const continueImportStep = useCallback(() => {
    resetMessages()

    if (destination.id === -1 || !file || !invitations.length) {
      showErrorMessage(t('Complete required fields'))
      return
    }

    saveImportedInvitations()
  }, [destination.id, file, invitations.length, resetMessages, saveImportedInvitations, showErrorMessage, t])

  const handleBackStep = useCallback(() => {
    setActiveStep(prev => Math.max(prev - 1, 0))
  }, [])

  const isVisitorStepMissingRequiredFields =
    (ask_visit_identification && !identification.trim()) ||
    !name.trim() ||
    !lastNameOne.trim() ||
    !email.trim() ||
    !validateEmail(email) ||
    (ask_visit_comes_from && !comesFrom.trim()) ||
    destination.id === -1

  const resetForm = useCallback(() => {
    setTab(VISIT)
    setDate(today)
    setCalendarDate(today)
    setOpenTimePicker(false)
    setEntryTime(DEFAULT_ENTRY_TIME)
    setExitTime(DEFAULT_EXIT_TIME)
    setEntryTimePickerValue(buildInitialTime(DEFAULT_ENTRY_TIME))
    setExitTimePickerValue(buildInitialTime(DEFAULT_EXIT_TIME))
    setIdentification('')
    setName('')
    setLastNameOne('')
    setLastNameTwo('')
    setEmail('')
    setComesFrom('')
    setLicensePlate('')
    setNotes('')
    setVisitReason('')
    setDestinationId('')
    setEmailError('')
    setLicensePlateError('')
    setFile(null)
    setInvitations([])
    setActiveStep(0)
    setSuccessItem(null)
    setAccessControlQR(null)
    setSuccessSyncStatus('synced')
  }, [])

  const EntryTimeIsLaterEqualThanExitTime = useCallback(() => {
    if (!entryTimePickerValue || !exitTimePickerValue) return false
    const entry = formatDateToTimeISO(entryTimePickerValue)
    const exit = formatDateToTimeISO(exitTimePickerValue)
    return (entry >= exit)
  }, [exitTimePickerValue, entryTimePickerValue])

  const getMinTime = useCallback(() => {
    const day = multiDay && Array.isArray(date) ? date[0] : date
    if (!day || !isTodayFromJsDate(day)) return null
    return getCurrentHour(0, 5)
  }, [date, multiDay])

  const EntryTimeIsEarlierMinTime = useCallback(() => {
    const minTime = getMinTime()

    if (!entryTimePickerValue || !minTime) return false
    const entry = formatDateToTimeISO(entryTimePickerValue)
    const min = formatDateToTimeISO(minTime)

    return (entry <= min)
  }, [getMinTime, entryTimePickerValue])
  const disabledTimePicker = useCallback(() => {
    return (
      EntryTimeIsLaterEqualThanExitTime() ||
      EntryTimeIsEarlierMinTime()
    )
  }, [EntryTimeIsLaterEqualThanExitTime, EntryTimeIsEarlierMinTime])

  const renderTimePickerMessage = useCallback(() => {
    if (EntryTimeIsLaterEqualThanExitTime()) {
      return (
        <MessageBox
          message={t('Check out time must be greater than check in time')}
          type='error'
        />
      )
    }
    if (EntryTimeIsEarlierMinTime()) {
      return (
        <MessageBox
          message={t('It is not possible to select past times. Please select a valid time')}
          type='error'
        />
      )
    }

    return <p className='message'></p>
  }, [EntryTimeIsLaterEqualThanExitTime, EntryTimeIsEarlierMinTime, t])

  const onSearchByCRCI = useCallback(async (value) => {
    if (!value?.trim()) return null

    setLoading(true)
    try {
      const response = window.electronAPI
        ? await window.electronAPI.onSearchCRPadron(value)
        : await api.get(`cr/person/${value}`)

      defaultSuccess(response)
      const { data } = response
      if (!data?.person?.id) return null

      const {
        nombre,
        apellido_1,
        apellido_2,
      } = data.person

      setName(nombre)
      setLastNameOne(apellido_1)
      setLastNameTwo(apellido_2)
    } catch (error) {
      defaultCatch(error)
    }
  }, [defaultCatch, defaultSuccess, setLoading])

  const onEfficientSearch = useMemo(
    () => debounce((nextValue) => onSearchByCRCI(nextValue), 1000),
    [onSearchByCRCI]
  )

  const onChangeIdentification = useCallback((value) => {
    setIdentification(value)
    if (isCostaRica) onEfficientSearch(value)
  }, [isCostaRica, onEfficientSearch])

  const visitorTab = (
    <div className='new-visitor-section new-visitor-section--tabs'>
      <div className='form-wrapper workplace form-wrapper--visitor-tall'>
        <Stack spacing={2} direction='column'>
        {ask_visit_identification && (
            <Stack {...stackProps}>
              <div className='visitor-field-header'>
                <PageviewIcon className='field-icon' />
                <label className='visitor-label' htmlFor='visit-identification'>
                  {`${identificationLabel} (${t('Required')})`}
                </label>
              </div>
              <TextField
                id='visit-identification'
                className='visitor-input'
                variant='filled'
                value={identification}
                onChange={e => onChangeIdentification(e.target.value)}
                fullWidth={!isLarge}
                placeholder={t('Write here')}
                hiddenLabel
              />
            </Stack>
        )}

          <Stack {...stackProps}>
            <div className='visitor-field-header'>
              <PersonIcon className='field-icon' />
              <label className='visitor-label' htmlFor='visit-name'>
                {`${t('name')} (${t('Required')})`}
              </label>
            </div>
            <TextField
              id='visit-name'
              className='visitor-input'
              variant='filled'
              value={name}
              onChange={e => setName(e.target.value)}
              fullWidth={!isLarge}
              placeholder={t('Write here')}
              hiddenLabel
            />
          </Stack>

          <Stack {...stackProps}>
            <div className='visitor-field-header'>
              <PersonIcon className='field-icon' />
              <label className='visitor-label' htmlFor='visit-last-name-one'>
                {`${t('Last name one')} (${t('Required')})`}
              </label>
            </div>
            <TextField
              id='visit-last-name-one'
              className='visitor-input'
              variant='filled'
              value={lastNameOne}
              onChange={e => setLastNameOne(e.target.value)}
              fullWidth={!isLarge}
              placeholder={t('Write here')}
              hiddenLabel
            />
          </Stack>

          <Stack {...stackProps}>
            <div className='visitor-field-header'>
              <PersonIcon className='field-icon' />
              <label className='visitor-label' htmlFor='visit-last-name-two'>
                {t('Last name two')}
              </label>
            </div>
            <TextField
              id='visit-last-name-two'
              className='visitor-input'
              variant='filled'
              value={lastNameTwo}
              onChange={e => setLastNameTwo(e.target.value)}
              fullWidth={!isLarge}
              placeholder={t('Write here')}
              hiddenLabel
            />
          </Stack>

          <Stack {...stackProps}>
            <div className='visitor-field-header'>
              <AlternateEmailIcon className='field-icon' />
              <label className='visitor-label' htmlFor='visit-email'>
                {`${t('email')} (${t('Required')})`}
              </label>
            </div>
            <TextField
              id='visit-email'
              className='visitor-input'
              variant='filled'
              value={email}
              error={!!emailError}
              helperText={emailError}
              onChange={e => {
                setEmail(e.target.value)
                setEmailError('')
              }}
              fullWidth={!isLarge}
              placeholder={t('Write here')}
              hiddenLabel
              onBlur={() => {
                if (!email) return
                if (!validateEmail(email)) {
                  setEmailError(t('invalid email'))
                  return
                }
                const normalizedEmail = getNormalizedEmailList(email)
                if (normalizedEmail !== email) setEmail(normalizedEmail)
              }}
            />
          </Stack>

        {ask_visit_license_plate && (
            <Stack {...stackProps}>
              <div className='visitor-field-header'>
                <DirectionsCarFilledOutlinedIcon className='field-icon' />
                <label className='visitor-label' htmlFor='visit-license-plate'>
                  {t('license_plate')}
                </label>
              </div>
              <TextField
                id='visit-license-plate'
                className='visitor-input'
                variant='filled'
                value={licensePlate}
                error={!!licensePlateError}
                helperText={licensePlateError}
                onChange={e => {
                  const value = e.target.value || ''
                  if (value.length > MAX_LICENSE_PLATE_LENGTH) return
                  setLicensePlate(value.toUpperCase())
                  setLicensePlateError('')
                }}
                fullWidth={!isLarge}
                placeholder={t('Write here')}
                hiddenLabel
                onBlur={() => {
                  if (licensePlate && !validateLicensePlate(licensePlate)) setLicensePlateError(t('invalid license plate'))
                }}
              />
            </Stack>
        )}

        {ask_visit_comes_from && (
            <Stack {...stackProps}>
              <div className='visitor-field-header'>
                <TransportationIcon className='field-icon' />
                <label className='visitor-label' htmlFor='visit-comes-from'>
                  {`${t('Comes from')} (${t('Required')})`}
                </label>
              </div>
              <TextField
                id='visit-comes-from'
                className='visitor-input'
                variant='filled'
                value={comesFrom}
                onChange={e => setComesFrom(e.target.value)}
                fullWidth={!isLarge}
                placeholder={t('Write here')}
                hiddenLabel
              />
            </Stack>
        )}

          <Stack {...stackProps}>
            <div className='visitor-field-header'>
              <LocationOnIcon className='field-icon' />
              <label className='visitor-label' htmlFor='visit-destination'>
                {`${t('Go to')} (${t('Required')})`}
              </label>
            </div>
            <FormControl className='company-selector'>
              <Select
                id='visit-destination'
                variant='filled'
                value={destinationId}
                onChange={e => setDestinationId(e.target.value)}
                displayEmpty
                MenuProps={destinationMenuProps}
              >
                <MenuItem value='' disabled>
                  <em>{t('Select an option')}</em>
                </MenuItem>
                {visitDestinations.map(item => (
                  <MenuItem key={item.id} value={String(item.id)}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {enabledInvitationHostNotes &&
            <Stack {...stackProps} alignItems='start' className="visitor-field-row invitation-notes">
              <Stack alignItems='start' direction='row' gap={1} className='visitor-field-header'>
                <MenuIcon className='field-icon' />
                <label className="visitor-label" htmlFor="notes">
                  {t('Notes for visit')}
                </label>
              </Stack>
              <div className='visitor-input visitor-input--notes-wrapper'>
                <TextField
                  id="notes"
                  className='visitor-input--notes'
                  value={hostNotes}
                  onChange={e => evalSetHostNotes(e.target.value)}
                  variant="filled"
                  autoComplete="off"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: MAX_NOTES_LENGTH }}
                  fullWidth
                  placeholder={t('Write here')}
                  hiddenLabel
                />
                <span className='visitor-input--notes-counter'>
                  {hostNotes.length}/{MAX_NOTES_LENGTH}
                </span>
              </div>
            </Stack>
          }
        </Stack>
      </div>
      <div className='new-visitor-footer'>
        <Button
          fullWidth={!isLarge}
          variant='contained'
          disabled={isVisitorStepMissingRequiredFields}
          onClick={continueVisitorStep}
        >
          {t('continue')}
        </Button>
      </div>
    </div>
  )

  const importTab = (
    <div className='new-visitor-section new-visitor-section--tabs'>
      <div className='new-visitor-card'>
        <Stack spacing={6} direction={{ xs: 'column', md: 'row' }}>
        <div className='import-info'>
          <p>{t('Considerations of the file to import')}</p>
          <ul>
            <li>{t('Allowed format: Excel (.xlsx).')}</li>
            <li>{t('Only one file can be uploaded at a time.')}</li>
            <li>{t('The data must be organized according to the format of the following template:')}</li>
          </ul>
          <a
            href={ask_visit_identification ? '/pre_visitor_registration_template.xlsx' : '/pre_visitor_registration_template_without_identification.xlsx'}
            download
          >
            <FileDownloadIcon sx={{ fontSize: 15 }} />
            {t('Downloadable template')}
          </a>
        </div>

        <Stack className='stack-import' spacing={2}>
          <label className='pre-label-form'>
            1. {t('Select the date.')}
          </label>
          <Grid container spacing={isLarge ? 0 : 5} className='form-input-group'>
            <Grid item xs={12} md>
              <FormControl fullWidth>
                <TextField
                  value={getDateLabel()}
                  onClick={handleOpenDatePicker}
                  variant='filled'
                  fullWidth
                />
              </FormControl>
            </Grid>
            <Divider orientation='vertical' variant='middle' flexItem />
            <Grid item xs={12} md>
              <FormControl fullWidth>
                <TextField
                  value={`${formatDisplayTime(entryTime)} - ${formatDisplayTime(exitTime)}`}
                  onClick={handleOpenTimePicker}
                  disabled={!getDateLabel()}
                  variant='filled'
                  fullWidth
                />
              </FormControl>
            </Grid>
          </Grid>
          <label className='pre-label-form'>
            2. {t('Go to')}
          </label>
          <FormControl fullWidth>
            <Select
              variant='filled'
              value={destinationId}
              onChange={e => setDestinationId(e.target.value)}
              displayEmpty
            >
              <MenuItem value='' disabled>
                <em>{t('select')}</em>
              </MenuItem>
              {visitDestinations.map(item => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <label className='pre-label-form'>
            3. {t('Upload the file in XLSX format.')}
          </label>
          <DragAndDrop
            accept='.xlsx'
            disabled={false}
            file={file}
            onChange={handleFileSelection}
          />

          {!!invitations.length && (
            <Alert severity='info'>
              {t('Imported rows ready: {{count}}', { count: invitations.length })}
            </Alert>
          )}
        </Stack>
        </Stack>
      </div>
      <div className='new-visitor-footer'>
        <Button
          fullWidth={!isLarge}
          variant='contained'
          disabled={destination.id === -1 || !file || !invitations.length}
          onClick={continueImportStep}
        >
          {t('continue')}
        </Button>
      </div>
    </div>
  )

  const detailStep = (
    <div className='new-visitor-section-details'>
      <Stack>
        <h2>{t('visit details')}</h2>
        <label className='pre-label-form'>
          {t('When will you receive this person?')}
        </label>
      </Stack>
      <div>
        <Stack direction='column'>
          <Stack className='form-input-group' direction={isLarge ? 'row' : 'column'} spacing={isLarge ? 0 : 5} alignItems='flex-end'>
            <Stack flex={1} width='100%'>
              <FormControl fullWidth>
                <InputLabel shrink>{t('date')}</InputLabel>
                <TextField
                  value={getDateLabel()}
                  onClick={handleOpenDatePicker}
                  variant='filled'
                  fullWidth
                />
              </FormControl>
            </Stack>
            <Divider orientation='vertical' variant='middle' flexItem />
            <Stack flex={1} width='100%'>
              <FormControl fullWidth>
                <InputLabel shrink>{t('hour')}</InputLabel>
                <TextField
                  value={`${formatDisplayTime(entryTime)} - ${formatDisplayTime(exitTime)}`}
                  onClick={handleOpenTimePicker}
                  disabled={!getDateLabel()}
                  variant='filled'
                  fullWidth
                />
              </FormControl>
            </Stack>
          </Stack>
          <label className='visitors-reason-pre-label'>
            {t("What is the reason for the visit?")}
          </label>
          <Stack flexDirection='column' gap={0.5}>
            <label className='visitors-reason-label' htmlFor='visit-reason'>
              {t("reason")}
            </label>
            <FormControl className='reason-selector'>
              <Select
                id='visit-reason'
                variant='filled'
                value={visitReason}
                onChange={handleVisitReasonChange}
                displayEmpty
              >
                {defaultMenuItem()}
                {reasons.map(item => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {enabledInvitationNotes && (
            <Stack className='invitation-notes' width='100%'>
              <label className='visitors-reason-pre-label'>
                {`${t('preregistration_reception_notes')} ${t('(optional)')}`}
              </label>
              <Stack className='form-input-group' width='100%' direction={isLarge ? 'row' : 'column'} spacing={isLarge ? 0 : 5}>
                <Stack flex={1} width='100%'>
                  <FormControl fullWidth className='visitor-notes-wrapper'>
                    <InputLabel shrink className='notes-label'>{`${t('preregistration_reception_notes')} ${t('(optional)')}`}</InputLabel>
                    <TextField
                      className='visitor-invitations-notes'
                      value={notes}
                      onChange={e => {
                        if (e.target.value.length <= MAX_NOTES_LENGTH) setNotes(e.target.value)
                      }}
                      variant='filled'
                      autoComplete='off'
                      multiline
                      rows={4}
                      fullWidth
                    />
                    <span className='visitor-notes-counter'>
                      {notes.length}/{MAX_NOTES_LENGTH}
                    </span>
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
      </div>
      <div className='new-visitor-footer'>
        <Button
          fullWidth={!isLarge}
          variant='contained'
          onClick={tab === VISIT ? saveSingleInvitation : saveImportedInvitations}
        >
          {t('Confirm pre-registration')}
        </Button>
      </div>
    </div>
  )

  const importSuccessStep = (
    <Stack spacing={4} alignItems='center' justifyContent='center'>
      <Alert severity={successSyncStatus === 'pending' ? 'info' : 'success'}>
        {successSyncStatus === 'pending'
          ? t('Pre-register pending synchronization')
          : t('Multiple pre-registration has been successfully created.')}
      </Alert>
      <Stack direction='column' spacing={2} width='100%' maxWidth={420}>
        <Button
          variant='outlined'
          size='large'
          onClick={resetForm}
        >
          {t('NEW PRE REGISTRATION')}
        </Button>
        <Button
          variant='outlined'
          size='large'
          onClick={() => {
            if (onCreated) onCreated()
          }}
        >
          {t('SEE PRE REGISTRATIONS')}
        </Button>
      </Stack>
    </Stack>
  )

  const handleDownloadQR = useCallback(() => {
    downloadQRAsImage(
      name || 'visitor',
      getFormateDate({ date: Array.isArray(date) ? date[0] : date, multiDay: Array.isArray(date), t }),
      undefined,
      { infoMessageForDownload: t('qr_valid_for_all_days') }
    )
  }, [date, name, t])

  return (
    <LayoutCreate title={t('Guest pre-register')} goBack={onCancel}>
      <div className='NewVisitor'>
      <Stack spacing={3} className='new-visitor-layout'>
        <Stack direction='row' justifyContent='center' alignItems='center' position='relative' width='100%'>
          {activeStep === 1 && !isImportFlow && (
            <Button
              color='inherit'
              onClick={handleBackStep}
              sx={{ position: 'absolute', left: 0, gap: 1, minWidth: 'auto', height: 36 }}
            >
              <ArrowBackIcon color='primary' sx={{ height: 18, width: 18 }}/>
              {t('back')}
            </Button>
          )}
          <CustomizedStepper activeStep={stepperActiveStep} totalSteps={stepperTotalSteps} />
        </Stack>

        {isVisitorSuccessStep ? (
          <Stack spacing={3} alignItems='center'>
            <Alert severity={successSyncStatus === 'pending' ? 'info' : 'success'}>
              {successSyncStatus === 'pending'
                ? t('Pre-register pending synchronization')
                : t('Pre-register created successfully')}
            </Alert>
            <PrintVisit
              qr={accessControlQR}
              item={successItem || {}}
              date={Array.isArray(date) ? date[0] : date}
              entry_time={buildDateTime(Array.isArray(date) ? date[0] : date, entryTime)}
              exit_time={buildDateTime(Array.isArray(date) ? date[0] : date, exitTime)}
              infoMessage={t('You can view the QR again from the reservation details.')}
              multiDay={Array.isArray(date)}
              notes={notes}
              hostNotes={hostNotes}
            />
            <Stack direction='column' spacing={2} width='100%' maxWidth={420}>
              <Button
                variant='outlined'
                disabled={!accessControlQR}
                onClick={handleDownloadQR}
                style={{
                  textTransform: 'uppercase'
                }}
              >
                {t('Download')}
              </Button>
              <Button
                variant='outlined'
                onClick={resetForm}
                size='large'
              >
                {t('NEW PRE REGISTRATION')}
              </Button>
              <Button
                variant='outlined'
                onClick={() => {
                  if (onCreated) onCreated()
                }}
              >
                {t('EXIT')}
              </Button>
            </Stack>
          </Stack>
        ) : isImportSuccessStep ? (
          importSuccessStep
        ) : activeStep === 1 ? (
          detailStep
        ) : (
          <>
            <TabContext value={tab}>
              <TabList className='new-visit-tabs-container' onChange={handleTabChange}>
                <Tab label={t('Visitor')} value={VISIT} className='new-visit-tabs-tab'/>
                <Tab label={t('Import list')} value={IMPORT} className='new-visit-tabs-tab'/>
              </TabList>
            </TabContext>
            <div key={tab} className='tab-content-transition'>
              {tab === VISIT ? visitorTab : importTab}
            </div>
          </>
        )}
      </Stack>
      <SwipeableCalendar
        type={Array.isArray(date) ? 'range' : 'basic'}
        minDate={today}
        date={calendarDate}
        multiDay={Array.isArray(date)}
        open={openCalendar}
        onChange={setCalendarDate}
        setDate={setCalendarDate}
        setOpen={setOpenCalendar}
        onChangeMultiDay={onChangeMultiDay}
        onContinue={onContinueCalendar}
        onClose={onCloseCalendar}
        enabledMultiDay={tab === VISIT || tab === IMPORT}
        clearable
      />
      <SwipeableTimePicker
        type='range'
        open={openTimePicker}
        setOpen={setOpenTimePicker}
        showAllTime={false}
        allTime={false}
        time={entryTimePickerValue}
        exitTime={exitTimePickerValue}
        setTime={setEntryTimePickerValue}
        setExitTime={setExitTimePickerValue}
        onClose={onCloseTimePicker}
        onContinue={onContinueTimePicker}
        isDisabled={disabledTimePicker()}
        renderMessage={renderTimePickerMessage}
      />
      </div>
    </LayoutCreate>
  )
}

const mapStateToProps = state => ({
  ask_visit_comes_from: state.profile.ask_visit_comes_from,
  ask_visit_identification: state.profile.ask_visit_identification,
  ask_visit_license_plate: state.profile.ask_visit_license_plate,
  building_id: state.profile.building_id,
  companies: state.profile.companies,
  custom_visit_identification_label: state.profile.custom_visit_identification_label,
    isCostaRica: !!state.profile.enabled_costa_rica,
  isOnline: state.utilities.isOnline,
  visitDestinations: state.profile.visitDestinations,
  workplaceDefaultEntryTime: getWorkplaceDefaultEntryTime(state),
  workplaceDefaultExitTime: getWorkplaceDefaultExitTime(state),
})

const mapDispatchToProps = {
  defaultCatch,
  defaultSuccess,
  setLoading,
  setSnackbar,
}

export default connect(mapStateToProps, mapDispatchToProps)(NewVisitor)
